import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ThemeProvider } from '@mui/material/styles';
import { CustomTextField } from '../../../../utils/StyledInputs';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../../../../utils/Buttons';
import { PaginationAPI, theme } from '../../../../utils/TableStyles';
import { useUser } from '../context/UserContext';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import API from '../../../../api';
import { usePopup } from '../../../../context/PopupContext';

export default function NotesTab({ userID }) {
  const { isLoading, errorMessage } = useUser();
  const { showPopup, setPopUpText } = usePopup();

  const [notes, setNotes] = useState([]);
  const [isNotesApiLoading, setIsNotesApiLoading] = useState(true);
  const [apiResponseData, setApiResponseData] = useState({});
  const [authError, setAuthError] = useState('');

  const [newNote, setNewNote] = useState('');

  /* ------------------------------- Pagination ------------------------------- */

  const [notesPage, setNotesPage] = useState(0);
  const notesRowsPerPage = 10;

  const handleChangeNotesPage = (event, newPage) => {
    setNotesPage(newPage);
  };

  /* ------------------------------ Note Handling ----------------------------- */

  useEffect(() => {
    getNotesList();
  }, []);

  const handleStoreNote = () => {
    const data = {
      user_id: userID,
      note: newNote,
    };

    API.post(`/usernote`, data)
      .then((response) => {
        getNotesList();
        setNewNote('');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const getNotesList = () => {
    setAuthError('');

    API.get(`usernote/${userID}`)
      .then((response) => {
        setApiResponseData(response.data);
        setNotes(response.data.data);
      })
      .catch((error) => {
        if (error?.response && error.response?.status === 403) {
          setAuthError(error?.response?.data?.message || 'Utente non autorizzato');
        } else {
          setPopUpText("Errore nel caricamento delle entità associate all'utente.");
          showPopup(false);
        }
      })
      .finally(() => setIsNotesApiLoading(false));
  };

  useEffect(() => {
    if (Object.keys(apiResponseData).length > 0) {
      setNotes(apiResponseData.data);
      setNotesPage(apiResponseData.current_page - 1);
    }
  }, [apiResponseData]);

  /* -------------------------------- Edit Note ------------------------------- */

  const [noteToEdit, setNoteToEdit] = useState({});
  const [openEditNote, setOpenEditNote] = useState(false);

  const handleOpenEditNote = (note) => {
    setNoteToEdit(note);
    setOpenEditNote(true);
  };

  const handleCloseEditNote = () => {
    setNoteToEdit({});
    setOpenEditNote(false);
  };

  const handleChangeNoteToEdit = (e) => {
    setNoteToEdit({ ...noteToEdit, note: e.currentTarget.value });
  };

  const handleUpdateNote = () => {
    const data = {
      note: noteToEdit.note,
      id: noteToEdit.id,
    };

    API.put(`/usernote`, data)
      .then((response) => {
        getNotesList();
        handleCloseEditNote();
        showPopup(true, 'Nota aggiornata con successo');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  return (
    <>
      {isLoading ? (
        LoadingUI()
      ) : authError.length > 0 ? (
        ErrorUI(authError)
      ) : errorMessage.length === 0 ? (
        <>
          <Grid container spacing={3} sx={{ marginTop: '24px' }}>
            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
              <Card sx={{ padding: '24px' }}>
                <Typography variant="h6">Nuova nota</Typography>

                <Grid container spacing={2} sx={{ marginTop: '12px' }}>
                  <Grid item xs={12}>
                    <CustomTextField
                      value={newNote}
                      onChange={(e) => {
                        setNewNote(e.currentTarget.value);
                      }}
                      multiline
                      fullWidth
                      minRows={3}
                      label="Nota"
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <DefaultContainedButton
                      onClick={() => {
                        handleStoreNote();
                      }}
                    >
                      Aggiungi nota
                    </DefaultContainedButton>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ marginTop: '24px' }}>
            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
              <Card sx={{ position: 'relative' }}>
                <TableContainer sx={{ padding: '24px 24px 6px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo Nota</TableCell>
                        <TableCell>Nota</TableCell>
                        <TableCell>Utente Creazione</TableCell>
                        <TableCell>Entità Creazione</TableCell>
                        <TableCell>Data Creazione</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    {isNotesApiLoading ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : notes?.length > 0 ? (
                      <TableBody>
                        {notes.map((note) => (
                          <TableRow key={note.id}>
                            <TableCell>{note.note_type.type}</TableCell>
                            <TableCell
                              sx={{
                                whiteSpace: 'pre-line',
                                wordBreak: 'break-word',
                                maxWidth: 300,
                              }}
                            >
                              {note.note}
                            </TableCell>
                            <TableCell>
                              {note.user_created?.name} {note.user_created?.surname}
                            </TableCell>
                            <TableCell>{note.entity.name}</TableCell>
                            <TableCell>{dayjs(note.created_at).format('DD/MM/YYYY')}</TableCell>
                            <TableCell>
                              <Tooltip
                                title="Modifica Nota (modificabile fino a 7 giorni dall'inserimento)"
                                arrow
                                placement="top"
                              >
                                <Box
                                  sx={{
                                    display: note.is_editable ? 'flex' : 'none',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <CustomIconButton
                                    disabled={!note.is_editable}
                                    onClick={() => handleOpenEditNote(note)}
                                  >
                                    <EditIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* {emptyNotesRows > 0 && (
                          <TableRow style={{ height: 55 * emptyNotesRows }}>
                            <TableCell colSpan={5} />
                          </TableRow>
                        )} */}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2">Nessuna nota disponibile</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider theme={theme}>
                          <TablePagination
                            sx={{ borderBottom: 'none' }}
                            rowsPerPageOptions={[]}
                            colSpan={6}
                            count={apiResponseData?.total || 0}
                            rowsPerPage={notesRowsPerPage}
                            page={notesPage}
                            onPageChange={handleChangeNotesPage}
                            labelRowsPerPage="Righe per Pagina"
                            ActionsComponent={(props) => (
                              <PaginationAPI
                                {...props}
                                elements={10}
                                apiResponseData={apiResponseData}
                                setApiResponseData={setApiResponseData}
                                lastPage={apiResponseData?.last_page}
                              />
                            )}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Card>

              <Dialog open={openEditNote} onClose={handleCloseEditNote} fullWidth>
                <DialogTitle>Modifica Nota</DialogTitle>
                <DialogContent>
                  <Grid container spacing={2} sx={{ marginTop: '12px' }}>
                    <Grid item xs={12}>
                      <CustomTextField
                        value={noteToEdit.note}
                        multiline
                        minRows={3}
                        onChange={(e) => handleChangeNoteToEdit(e)}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <DefaultOutlinedButton onClick={() => handleCloseEditNote()}>Chiudi</DefaultOutlinedButton>
                  <DefaultContainedButton onClick={() => handleUpdateNote()}>Salva</DefaultContainedButton>
                </DialogActions>
              </Dialog>
            </Grid>
          </Grid>
        </>
      ) : (
        ErrorUI(errorMessage)
      )}
    </>
  );
}
